import { useEffect, useMemo, useState } from 'react';
import { navigate, switchTab } from '../../app/navigation';
import { displayAddress, isInspectionLocked, type V2Inspection } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { buildCloseout } from '../../state/closeout';
import { liveRowFacts } from '../../state/rowFacts';
import { useStore } from '../../state/createStore';
import { findingStore } from '../../state/findingStore';
import { inspectionStore } from '../../state/inspectionStore';
import { reportStore } from '../../state/reportStore';
import { Button, EmptyState, InlineMessage, ListRow, SectionHeader, TopBar } from '../../ui/components';
import { reportCloudStore, refreshCloudReports } from '../../state/reportCloudStore';
import { sessionStore } from '../../state/sessionStore';
import { openCloudReport } from './openCloudReport';
import { reportFileIcon, reportFileLabelKey } from './variantLabels';
import { formatDate } from '../../ui/hooks';
import { CoverThumb } from '../inspections/CoverPhotoTile';
import { CoverPhotoViewer } from '../inspections/CoverPhotoViewer';

// Reports tab root (task book §19): every inspection with its report state — generated (N files),
// ready to generate, or still with gaps — each opening that inspection's report screen.
export function ReportsScreen() {
  const { t, tp } = useT();
  const inspectionState = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const reports = useStore(reportStore, (s) => s.reports);
  // Owner 2026-09-14: the cover thumbnail in front of a row opens the full-size viewer, not the report.
  const [viewer, setViewer] = useState<V2Inspection | null>(null);
  // 2026-10-19 (Owner items 25 / 27): the delivered PDFs kept in the account, read-only — on a new phone or
  // after a reinstall they are here even though nothing is on the device.
  const cloud = useStore(reportCloudStore, (s) => s);
  const signedIn = useStore(sessionStore, (s) => s.phase === 'signedIn' && s.userId !== null);
  const [openError, setOpenError] = useState<string | null>(null);
  useEffect(() => {
    if (signedIn) void refreshCloudReports();
  }, [signedIn]);
  const cloudFiles = useMemo(() => [...cloud.files].sort((a, b) => b.generatedAt - a.generatedAt || a.fileName.localeCompare(b.fileName)), [cloud.files]);
  // Android acceptance 2026-10-21 (issue 15): before the account's answer arrives, a phone with nothing on
  // it flashed 「还没有报告」 and then the list. Say it is being read instead.
  const cloudLoading = signedIn && cloudFiles.length === 0 && (cloud.busy || cloud.state === 'unknown');

  // The file is fetched before it opens (a few seconds for a 12 MB report): the row says so, and a second
  // tap meanwhile does nothing (emulator check 2026-10-21 — nothing visible happened for ~3 s).
  const [opening, setOpening] = useState<string | null>(null);
  async function openFile(fileId: string, fileName: string) {
    if (opening) return;
    setOpenError(null);
    setOpening(fileId);
    try {
      const opened = await openCloudReport(fileId, fileName);
      if (!opened) setOpenError(fileId);
    } finally {
      setOpening(null);
    }
  }

  const cloudSection = signedIn ? (
    <div className="list" data-testid="cloud-reports">
      <SectionHeader
        title={t('cloud.listTitle')}
        meta={t('cloud.listMeta')}
        action={
          <Button variant="text" size="small" icon="refresh" onClick={() => void refreshCloudReports()} disabled={cloud.busy} testId="cloud-refresh">
            {cloud.busy ? t('cloud.loading') : t('cloud.refresh')}
          </Button>
        }
      />
      {cloud.state === 'notConfigured' ? (
        <p className="cloud-note" data-testid="cloud-not-configured">{t('cloud.noteNotConfigured')}</p>
      ) : cloudLoading ? (
        <p className="cloud-note" data-testid="cloud-loading">{t('cloud.loadingList')}</p>
      ) : cloudFiles.length === 0 ? (
        <p className="cloud-note" data-testid="cloud-empty">{cloud.state === 'offline' ? t('cloud.noteOffline') : t('cloud.listEmpty')}</p>
      ) : (
        cloudFiles.map((file) => (
          <div key={file.id}>
            <ListRow
              icon={reportFileIcon(file.variant)}
              title={file.fileName}
              subtitle={[file.address, t(reportFileLabelKey(file.variant)), tp('report.pages', file.pageCount), formatDate(file.generatedAt)].filter((part) => part.length > 0).join(' · ')}
              trailing={<span className="badge badge-muted">{opening === file.id ? t('cloud.opening') : t('cloud.open')}</span>}
              onClick={() => void openFile(file.id, file.fileName)}
              dataName={file.fileName}
              testId="cloud-report-row"
            />
            {openError === file.id ? <InlineMessage kind="err" text={t('cloud.openFailed')} /> : null}
          </div>
        ))
      )}
    </div>
  ) : null;
  const rows = useMemo(
    () =>
      inspectionState.inspections.map((inspection) => {
        const files = reports.filter((r) => r.inspectionId === inspection.id).length;
        const closeout = buildCloseout(inspection.id, inspectionState, findingState, liveRowFacts());
        return { inspection, files, blocking: closeout.blocking.length, locked: isInspectionLocked(inspection) };
      }),
    [inspectionState, findingState, reports]
  );

  return (
    <div className="screen" data-testid="reports-screen">
      <TopBar title={t('nav.reports')} subtitle={rows.length > 0 ? tp('inspections.onDevice', rows.length) : undefined} />
      {rows.length === 0 && cloudLoading ? (
        <div className="screen-body">
          <p className="cloud-note" data-testid="cloud-loading">
            {t('cloud.loadingList')}
          </p>
        </div>
      ) : rows.length === 0 && cloudFiles.length === 0 ? (
        <EmptyState
          icon="receipt_long"
          title={t('reports.emptyTitle')}
          body={t('reports.emptyBody')}
          action={
            <Button variant="secondary" onClick={() => switchTab('inspections')} testId="reports-go-inspections">
              {t('shell.goToInspections')}
            </Button>
          }
        />
      ) : rows.length === 0 ? (
        <div className="screen-body">{cloudSection}</div>
      ) : (
        <div className="screen-body">
          <div className="list" data-testid="reports-list">
            {rows.map(({ inspection, files, blocking, locked }) => (
              <ListRow
                key={inspection.id}
                leading={inspection.coverPhoto ? <CoverThumb inspection={inspection} onOpen={() => setViewer(inspection)} testId="report-cover-thumb" /> : undefined}
                icon={inspection.coverPhoto ? undefined : locked ? 'picture_as_pdf' : 'receipt_long'}
                iconTone={locked ? 'default' : 'soft'}
                title={displayAddress(inspection, t('inspection.noAddress'))}
                subtitle={`${formatDate(inspection.inspectionAt)} · ${locked ? tp('report.filesCount', files) : blocking > 0 ? t('closeout.openWithGaps', { n: blocking }) : t('reports.readyToGenerate')}`}
                // A generated report is a report state, never an item result: the frozen result
                // vocabulary (无异常 …) belongs to checklist rows only (audit 2026-09-11 §1.1).
                trailing={locked ? <span className="badge badge-generated">{t('reports.generated')}</span> : blocking > 0 ? <span className="count-dot">{blocking}</span> : undefined}
                chevron
                onClick={() => navigate({ screen: 'report', inspectionId: inspection.id })}
                dataName={displayAddress(inspection, t('inspection.noAddress'))}
                testId="report-row"
              />
            ))}
          </div>
          {cloudSection}
        </div>
      )}
      {viewer ? <CoverPhotoViewer inspection={viewer} onClose={() => setViewer(null)} /> : null}
    </div>
  );
}
